import { History, Location } from 'history';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import { FunnelType, StepDirection } from 'scenes/common/types';
import {
  QuoteNumberPrefixes,
  RetrieveQueryParamKeys,
  RetrieveStepProps,
} from 'scenes/retrieve/types';
import { route } from 'scenes/common/helpers/route';

import {
  useQuoteFlowDispatch,
  useFunnelState,
} from 'scenes/common/contexts/quoteFlowContext';
import {
  LOADING,
  isBusinessError,
  isApiError,
} from 'scenes/common/helpers/apiData/apiData';

import analytics from 'scenes/retrieve/analytics';
import {
  RetrieveApiResponse,
  RetrieveFunnelState,
  RetrieveFormAnswer,
} from 'scenes/retrieve/flows/types';

import api from 'scenes/retrieve/api/quote';
import { getCampaignQueryParams } from 'scenes/common/hooks/useMarketingCampaignParams';
import { useSystemConfig } from 'scenes/common/hooks/useSystemConfig';
import { useConfigActions } from 'scenes/common/hooks/useActions/useConfigActions';
import { isUnderMaintenance } from 'scenes/common/selectors/configSelectors';
import redirectToOutagePage from 'scenes/outage/helpers/config';

const getQuoteNumberQueryParam = (location: Location) => {
  const quoteNumber = new URLSearchParams(location.search).get(
    RetrieveQueryParamKeys.QuoteNumber
  );

  if (
    Object.values(QuoteNumberPrefixes).some((prefix) =>
      quoteNumber?.includes(prefix)
    )
  ) {
    return quoteNumber;
  }
  return null;
};

export const withRetrieveQuote = (
  Component: FunctionComponent<RetrieveStepProps>
): FunctionComponent => () => {
  const funnelState = useFunnelState() as RetrieveFunnelState;
  const dispatch = useQuoteFlowDispatch();
  const { productType } = useParams<{ productType: FunnelType }>();
  const history: History = useHistory();
  const { cid, qid } = getCampaignQueryParams(useLocation());

  const [quoteNumberFromQueryParams] = useState(
    getQuoteNumberQueryParam(useLocation())
  );

  const handleAnswer = useCallback(
    (answer: RetrieveFormAnswer) => {
      // Enable this if you need to debug actions
      // console.log('handleAnswer Received', answer, funnelState.step);
      dispatch({ ...answer, sourceStep: funnelState.step });
    },
    [dispatch, funnelState.step]
  );

  const apiResponse = (
    apiResponses: Partial<RetrieveApiResponse>
  ): RetrieveFormAnswer => ({
    direction: StepDirection.ApiResponse,
    formValues: {},
    apiResponses,
  });

  const onApiResponse = useCallback(
    (response: Partial<RetrieveApiResponse>) => {
      handleAnswer(apiResponse(response));
    },
    [handleAnswer]
  );

  useEffect(() => {
    if (
      isUnderMaintenance(funnelState.apiResponses.config, FunnelType.Retrieve)
    ) {
      redirectToOutagePage(history);
    }
  }, [funnelState, history]);

  const onRetrieveQuote = (
    quoteNumber: string,
    postcode: string,
    dob: string
  ) => {
    // console.log('Enter withRetrieveQuote.onRetrieveQuote');

    const prefix = quoteNumber.substr(0, 4);

    if (prefix === QuoteNumberPrefixes.COMO) {
      onApiResponse({
        motorQuoteDetails: LOADING,
      });
      api
        .retrieveMotorQuote(quoteNumber, postcode, dob)
        .then((motorQuoteDetails) => {
          onApiResponse({
            motorQuoteDetails,
          });
          return motorQuoteDetails;
        })
        .then((motorQuoteDetails) => {
          if (
            !isBusinessError(motorQuoteDetails) &&
            !isApiError(motorQuoteDetails)
          ) {
            analytics().retrieveQuoteStart(funnelState, quoteNumber);
            const path = route(FunnelType.Motor, '');
            history.push(path);
          }
        });
    } else if (prefix === QuoteNumberPrefixes.COHO) {
      onApiResponse({
        homeQuoteDetails: LOADING,
      });
      api
        .retrieveHomeQuote(quoteNumber, postcode, dob)
        .then((homeQuoteDetails) => {
          onApiResponse({
            homeQuoteDetails,
          });
          return homeQuoteDetails;
        })
        .then((homeQuoteDetails) => {
          if (
            !isBusinessError(homeQuoteDetails) &&
            !isApiError(homeQuoteDetails)
          ) {
            analytics().retrieveQuoteStart(funnelState, quoteNumber);
            history.push(route(FunnelType.Home, ''));
          }
        });
    } else if (prefix === QuoteNumberPrefixes.COLA) {
      onApiResponse({
        landlordQuoteDetails: LOADING,
      });
      api
        .retrieveLandlordQuote(quoteNumber, postcode, dob)
        .then((landlordQuoteDetails) => {
          onApiResponse({
            landlordQuoteDetails,
          });
          return landlordQuoteDetails;
        })
        .then((landlordQuoteDetails) => {
          if (
            !isBusinessError(landlordQuoteDetails) &&
            !isApiError(landlordQuoteDetails)
          ) {
            analytics().retrieveQuoteStart(funnelState, quoteNumber);
            history.push(route(FunnelType.Landlord, ''));
          }
        });
    }
  };

  useEffect(() => {
    history.push(route(productType, funnelState.step), {
      flow: funnelState.flow,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, funnelState.step, funnelState.flow, productType]);

  useEffect(() => {
    analytics().viewLoaded(funnelState, cid, qid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { onGetConfig } = useConfigActions(onApiResponse);
  // Enable maintenance page
  useSystemConfig(onGetConfig);

  return (
    <Component
      funnelState={funnelState}
      flowType={funnelState.flow}
      stepType={funnelState.step}
      onRetrieveQuote={onRetrieveQuote}
      defaultQuoteNumber={quoteNumberFromQueryParams}
    />
  );
};

export default withRetrieveQuote;
